import React, { useMemo, useState } from 'react';
import { fmtMoney } from '../lib/format';

// ═══════════════════════════════════════════════════════════════════════════
//  EARNINGS — money actually received, by month
//
//  Payments, not invoices. An invoice is a request; a payment is a fact, and a
//  chart that draws invoices tells a tech they earned money they have not been
//  handed. The two differ by whatever is outstanding, which on this platform is
//  the number that matters most.
//
//  ── Colour ────────────────────────────────────────────────────────────────
//  The mint (#52D094) measures 1.89:1 against a white surface. WCAG asks 3:1
//  of a graphical object, so mint cannot be the line — a tech reading this in a
//  van with sun on the screen would lose it. Mint is the gradient fill, where
//  it has no contrast duty, and the line is the deep green at 9.4:1. Both
//  numbers are measured, not judged.
//
//  One series, so no legend: the card's own title names it. The axis is two
//  end labels and a baseline rather than a grid — six months of monthly totals
//  do not need horizontal rules to be read.
// ═══════════════════════════════════════════════════════════════════════════

const W = 520;
const H = 170;
const PAD = { top: 14, right: 8, bottom: 22, left: 8 };

// Catmull-Rom through the points, converted to cubic Béziers. A polyline reads
// as jagged at this size and a quadratic smooth-to (Q…T) overshoots on a spike,
// drawing a month that earned more than it did.
function smoothPath(pts) {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
  let d = `M${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    d += ` C${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6}`
       + ` ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6}`
       + ` ${p2.x},${p2.y}`;
  }
  return d;
}

export default function EarningsChart({ months }) {
  const [hover, setHover] = useState(null);

  const { pts, area, line, max } = useMemo(() => {
    const values = months.map(m => m.total);
    // A flat run of zeros would divide by zero and put the line on the top
    // edge; a floor of 1 keeps it on the baseline where it belongs.
    const max = Math.max(1, ...values);
    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;
    const step = months.length > 1 ? innerW / (months.length - 1) : 0;
    const pts = months.map((m, i) => ({
      x: PAD.left + i * step,
      y: PAD.top + innerH - (m.total / max) * innerH,
      ...m,
    }));
    const line = smoothPath(pts);
    const base = PAD.top + innerH;
    const area = pts.length
      ? `${line} L${pts[pts.length - 1].x},${base} L${pts[0].x},${base} Z`
      : '';
    return { pts, area, line, max };
  }, [months]);

  // Nearest point to the pointer, in SVG units. getBoundingClientRect rather
  // than offsetX: the SVG scales to its container, so a pixel on screen is not
  // a unit in the viewBox.
  const track = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = ((clientX - rect.left) / rect.width) * W;
    let nearest = pts[0];
    pts.forEach(p => { if (Math.abs(p.x - x) < Math.abs(nearest.x - x)) nearest = p; });
    setHover(nearest);
  };

  return (
    <div className="qps-chart-placeholder">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Payments received over the last ${months.length} months. ${months.map(m => `${m.label}: ${fmtMoney(m.total)}`).join('. ')}`}
        onMouseMove={track}
        onMouseLeave={() => setHover(null)}
        onTouchStart={track}
        onTouchMove={track}
        onTouchEnd={() => setHover(null)}
      >
        <defs>
          <linearGradient id="qpsEarnFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#52D094" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#52D094" stopOpacity="0" />
          </linearGradient>
        </defs>

        <line
          x1={PAD.left} y1={H - PAD.bottom} x2={W - PAD.right} y2={H - PAD.bottom}
          stroke="#e5e7eb" strokeWidth="1" vectorEffect="non-scaling-stroke"
        />

        <path d={area} fill="url(#qpsEarnFill)" />
        <path
          d={line} fill="none" stroke="#12503A" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke"
        />

        {hover && (
          <>
            <line
              x1={hover.x} y1={PAD.top} x2={hover.x} y2={H - PAD.bottom}
              stroke="#111111" strokeOpacity="0.18" strokeWidth="1" vectorEffect="non-scaling-stroke"
            />
            {/* A 2px surface ring keeps the marker readable where it sits on
                top of its own fill. */}
            <circle cx={hover.x} cy={hover.y} r="5.5" fill="#ffffff" />
            <circle cx={hover.x} cy={hover.y} r="4" fill="#12503A" />
          </>
        )}
      </svg>

      <div className="qps-chart-labels">
        {months.map((m, i) => (
          <span key={i} className={hover && hover.label === m.label ? 'on' : ''}>{m.label}</span>
        ))}
      </div>

      {/* The readout sits under the plot rather than floating over it: a
          tooltip that follows a finger on a phone is a tooltip under a thumb. */}
      <div className="qps-chart-read">
        {hover
          ? <><b>{fmtMoney(hover.total)}</b> received in {hover.long}</>
          : <>Tap or hover the line for a month. Peak month {fmtMoney(max)}.</>}
      </div>
    </div>
  );
}
